import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbar } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { TrackerConfigTool } from 'src/app/classes/shared/tracker-config.tool';
import { TrackerTemplateEditor } from 'src/app/classes/shared/tracker-template-editor';
import { Pop } from 'src/app/classes/shared/pop';
import { PutTracker, Tracker } from 'src/app/interfaces/api/tracker';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { ButtonComponent } from 'ui-library';
import { TrackerConfigFormComponent } from '../../resources/tracker-config-form/tracker-config-form.component';

// Rapport KYD's own edit entry point - shows this tracker's own settings directly, ready to
// edit and save (written into tracker.settings, see TrackerConfigTool.saveSettings()) - no
// name. The template linked to the tracker (tracker.config) shows selected at the top and fills
// in any value missing from its settings; picking another one prefills the fields below with
// its saved values and links it on save. Creating/updating a template is a deliberately
// separate action ("Enregistrer comme modèle") - see onSaveAsTemplate().
@Component({
  selector: 'app-kyd-config-dialog',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    MatSelectModule,
    MatToolbar,
    ButtonComponent,
    TrackerConfigFormComponent,
  ],
  standalone: true,
  templateUrl: './kyd-config-dialog.component.html',
  styleUrls: ['./kyd-config-dialog.component.scss']
})

export class KydConfigDialogComponent implements OnInit, OnDestroy {
  // Services
  private readonly kydConfigTool: TrackerConfigTool = inject(TrackerConfigTool);
  private readonly pop: Pop = inject(Pop);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly trackerService: TrackerService = inject(TrackerService);
  private readonly data: { tracker: Tracker } = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<KydConfigDialogComponent> = inject(MatDialogRef<KydConfigDialogComponent>);

  private readonly destroy$ = new Subject<void>();

  // Variables
  tracker: Tracker = this.data.tracker;
  readonly type: NonNullable<TrackerConfig['type']> = (this.tracker.type as TrackerConfig['type']) ?? 'Unit';
  // Starts from the tracker's own settings - completed with its linked template's defaults once
  // configs have loaded (see ngOnInit()).
  readonly editor = new TrackerTemplateEditor(this.type, this.kydConfigTool.initialSettings(this.tracker));

  // Kept up to date by the same live subscription as the delete-detection below - reused for
  // the rename's own duplicate-name check rather than fetching a second time.
  private latestTrackers: Tracker[] = [];
  renaming: boolean = false;
  nameDraft: string = '';

  ngOnInit() {
    this.trackerService.getConfigs().pipe(takeUntil(this.destroy$)).subscribe((configs: TrackerConfig[]) => {
      const firstLoad = !this.editor.configs.length;
      this.editor.configs = configs || [];
      // Only on the first load - a later live emission (another template edited elsewhere)
      // must not discard fields the user is mid-editing here.
      if (firstLoad) this.editor.loadTracker(this.tracker);
    });

    // A delete from another session (web or mobile) while this dialog is open would otherwise
    // only surface as a 404/silent-desync on save - this closes the dialog as soon as it
    // happens instead, matching the same live-update mechanism kyd.component.ts's own table
    // already relies on (getTrackers() emits a fresh snapshot on every Tracker POST/PUT/DELETE,
    // from any client).
    this.trackerService.getTrackers().pipe(takeUntil(this.destroy$)).subscribe((trackers: Tracker[]) => {
      this.latestTrackers = trackers || [];

      if (trackers && !trackers.some(t => t.id === this.tracker.id)) {
        this.pop.warning(this.translate.instant('trackers.deleted_elsewhere'));
        this.dialogRef.close(false);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Live off the same subscription as the delete-detection above rather than this.tracker
  // (a static snapshot taken when the dialog opened) - a connection attempt can start from the
  // mobile app at any point while this dialog is already open, not just before it's opened.
  get isConnecting(): boolean {
    const live = this.latestTrackers.find(t => t.id === this.tracker.id) ?? this.tracker;
    return this.kydConfigTool.isConnecting(live);
  }

  // tracker.rawData is a plain {characteristicName: value} map (value is usually an array of
  // numbers, occasionally the literal string "not found" for a characteristic that failed to
  // read) - flattened into rows here rather than templated with a nested *ngFor, since the
  // value's own shape varies per characteristic.
  rawDataEntries(): { key: string; value: string }[] {
    if (!this.tracker.rawData) return [];
    return Object.entries(this.tracker.rawData).map(([key, value]) => ({
      key,
      value: Array.isArray(value) ? value.join(', ') : String(value),
    }));
  }

  startRename() {
    if (this.isConnecting) return;
    this.nameDraft = this.tracker.name;
    this.renaming = true;
  }

  cancelRename() {
    this.renaming = false;
  }

  confirmRename() {
    if (this.isConnecting) return;

    const trimmed = this.nameDraft?.trim();
    if (!trimmed || trimmed === this.tracker.name) {
      this.renaming = false;
      return;
    }

    // The two checks below only apply to actual KYD trackers - a Unit/Sigfox/etc. device has no
    // such naming convention on the mobile side to stay consistent with.
    if (this.tracker.type === 'KYD') {
      // [DEBUG] Every fake KYD created via unifield-mobile-2's debug menus is identified purely
      // by "FAKE" appearing somewhere in its name (isFakeKydTrackerName() in list-update-
      // kyds.page.ts, ble-detail.page.ts's own isFakeDevice()/setDeviceName() guard, etc.) -
      // renaming it away here would make it indistinguishable from a real KYD on the mobile
      // side too, not just here.
      if (this.tracker.name.includes('FAKE') && !trimmed.includes('FAKE')) {
        this.pop.warning(this.translate.instant('trackers.fake_device_name_must_contain_fake'));
        return;
      }

      // "KYD" is what identifies a tracker as one everywhere in the app (isKydTracker() on both
      // sides) - renaming it away would make an otherwise perfectly good KYD invisible to every
      // KYD-specific screen. Mirrors unifield-mobile-2's own ble-detail.page.ts setDeviceName().
      if (!trimmed.toUpperCase().includes('KYD')) {
        this.pop.warning(this.translate.instant('trackers.name_must_contain_kyd'));
        return;
      }
    }

    // Unlike creating a new KYD under a name that's already used (kyd-bulk-add-dialog.
    // component.ts), or the mobile app renaming an unregistered device into one (ble-detail.
    // page.ts's setDeviceName()), there is no "other" Tracker record to reuse/update here -
    // this one keeps its own id and history, and the backend enforces the same name-per-
    // project uniqueness on this PUT regardless, so "continuing anyway" could never actually
    // succeed. Blocked outright instead of offering a choice that wouldn't do anything.
    if (this.latestTrackers.some(t => t.name === trimmed && t.id !== this.tracker.id)) {
      this.pop.warning(this.translate.instant('trackers.tracker_already_exists'));
      return;
    }

    this.trackerService.setTracker({ ...this.tracker, name: trimmed } as PutTracker).subscribe(() => {
      this.tracker.name = trimmed;
      this.renaming = false;
      this.pop.toast(this.translate.instant('trackers.edit_success'), 'success', 4000, 'save');
    }, () => {
      this.pop.error(this.translate.instant('trackers.error.config_save'));
    });
  }

  save() {
    if (this.isConnecting) return;
    this.editor.save(() => this.pushToSensor());
  }

  // Saving as a template alone never touches the tracker - offers to apply it right away;
  // declining resets the form back to what the tracker actually has.
  onSaveAsTemplate() {
    if (this.isConnecting) return;
    this.editor.saveAsTemplate(() =>
      this.pop.confirm(this.translate.instant('trackers.apply_template_to_sensor_confirm'), (confirm: any) => {
        if (confirm.ok) this.pushToSensor();
        else this.editor.loadTracker(this.tracker);
      })
    );
  }

  private pushToSensor() {
    const { settings, offlineDelay } = this.editor.draft;
    const configId = this.editor.appliedTemplateId;

    this.kydConfigTool.saveSettings(this.tracker, settings, offlineDelay, configId).subscribe({
      next: () => {
        this.pop.toast(this.translate.instant('units.device.config_saved'), 'success', 4000, 'save');
        this.dialogRef.close(true);
      },
      error: (error: any) => this.editor.saveError(error),
    });
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
